<template>
    <div class="input_group">
        <label>{{ label }}</label>
        <div class="select_box">
            <template v-if="isDesktop">
                <span><i class="fa-solid fa-caret-down"></i></span>

                <div :id="'select-box-container-' + props.id" :class="{ 'select_error': errorMessage }">
                    <input :disabled="disabled" :id="props.id" autocomplete="off"
                        :class="'form-select border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-md shadow-sm mt-1 block w-full ' + [error ? 'danger-border' : '']"
                        :placeholder="placeholder" @focus="openBox" :value='search' @input='updateSearch'
                        :required="!!required" />

                    <ul class="select-box" :id="'select-box-' + props.id">
                        <template v-if="filteredList && Object.keys(filteredList).length > 0">
                            <li v-for="(i, key) in filteredList" :key="key" @click="selectItem(key)">{{ i.name }}</li>
                        </template>
                        <span class="message" v-else-if="search && !response">Nenhum
                            resultado encontrado para: <b>{{ search }}</b></span>
                        <span class="message" v-else-if="!search">Nenhum resultado encontrado</span>
                    </ul>

                </div>

            </template>

            <template v-else>
                <select :id="props.id" :class="'form-select select-input ' + [errorMessage ? 'danger-border' : '']"
                    @change="updateInput" :required="!!required" :disabled="disabled == 1">
                    <option v-if="placeholder" :selected="!hasSelection" disabled="disabled" hidden="hidden" value="">{{ placeholder }}
                    </option>
                    <option v-for="(item, key) in items" :key="key" :value="item.id" :selected="selectOptionOnMobile(item.id)">
                        {{
            item.name }}</option>
                </select>
            </template>
        </div>
        <span v-if="errorMessage" class="error_message">{{ errorMessage }}</span>
    </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, onMounted } from "vue";

const props = defineProps({
    modelValue: {},

    label: String,

    error: String,

    placeholder: {
        type: String,
        required: false,
        default: null
    },

    items: {
        required: true,
    },

    page: {
        type: String,
        required: false
    },

    required: {
        type: Boolean,
        required: false,
        default: null,
    },

    id: {
        type: String,
        required: true
    },

    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const boxIsOpen = ref(false);
const search = ref(null);
const response = ref(null);

function loadItem(newValue){    
    if ((newValue || newValue == 0) && props.items) {

        const selectedItem = props.items.find(item => (item.id !== null) && item.id.toString() === newValue.toString());
        if (selectedItem) {
            response.value = newValue;
            search.value = selectedItem.name;
        }
    } else {
        response.value = null;
        search.value = null;
    }
}

watch(() => props.modelValue, (value) => {    
    loadItem(value);
}, { immediate: true });

watch(() => props.items, () => {    
    loadItem(props.modelValue);
}, { immediate: true });

function resizeInput() {
    this.style.width = ((this.value.length + 1) * 8) + 'px';
}

function detectDesktop() {
    if (window.matchMedia) {
        return !window.matchMedia('(pointer: coarse)').matches;
    }

    const ua = navigator.userAgent;
    return !(/Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua));
}

const isDesktop = detectDesktop();

function updateSearch(e) {
    search.value = e.target.value;
    response.value = null;
}

const emit = defineEmits(["update:modelValue", "change"]);

watch([response, boxIsOpen], ([e]) => {
    emit("update:modelValue", e);
});

function updateInput(event) {
    const value = event.target.value;
    let selected = null;

    Object.keys(props.items ?? {}).forEach((key) => {
        const item = props.items[key];
        if (String(item.id ?? '') === value) {
            selected = item;
        }
    });

    const emitValue = selected ? selected.id : null;

    emit("update:modelValue", emitValue);
    emit("change", emitValue);
}

function selectOptionOnMobile(key) {
    if (Array.isArray(props.modelValue)) {
        return props.modelValue.includes(key);
    }
    return key == props.modelValue;
}

const hasSelection = computed(function () {
    let found = false;

    Object.keys(props.items ?? {}).forEach((key) => {
        if (selectOptionOnMobile(props.items[key].id)) {
            found = true;
        }
    });

    return found;
});

const filteredList = computed(function () {
    let list = {};

    if ((search.value === null && response.value === null) || response.value || response.value == 0) {
        return props.items;
    }

    if (Object.keys(filteredList).length) {

        Object.keys(props.items).forEach((key) => {
            if (search.value && props.items[key].name.toLowerCase().includes(search.value.toLowerCase())) {
                list = addToList(key, props.items[key], list);
            } else if (!search.value) {
                list = addToList(key, props.items[key], list);
            }
        });
        return list;
    }
    return {};
});

function addToList(key, item, list) {
    let currentItem = {
        ...list,
        [key]: item
    };
    list = currentItem;

    return list;
}

function openBox(e) {
    boxIsOpen.value = true;
    e.target.focus();
    e.target.select();
}

watch(boxIsOpen, function (e) {
    const box = document.getElementById("select-box-" + props.id);

    if (e) {
        if (box) {
            box.style.display = "block";
        }
        window.addEventListener('click', clickOutOfTheBox, true);
        window.addEventListener('scroll', clickOutOfTheBox, true);
    } else {
        if (box) {
            box.style.display = "none";
        }
        if ((response.value == '' || response.value == [] || response.value == undefined) && response.value != 0) {
            search.value = null;
        }
        window.removeEventListener('click', clickOutOfTheBox, true);
        window.removeEventListener('scroll', clickOutOfTheBox, true);
    }
});

function clickOutOfTheBox(e) {
    if (document.getElementById('select-box-container-' + props.id) && !document.getElementById('select-box-container-' + props.id).contains(e.target)) {
        boxIsOpen.value = false;
    }
}

onMounted(() => {
    window.removeEventListener('click', clickOutOfTheBox, true);
    window.removeEventListener('input', resizeInput, true);
});

function selectItem(r) {
    const selectedId = props.items[r].id;
    response.value = selectedId;
    search.value = props.items[r].name;
    boxIsOpen.value = false;
    emit('update:modelValue', selectedId);
    emit('change', selectedId);

    handleError();
}

const errorMessage = ref(props.error);

watch(() => props.error, (value) => {
    if (value) {
        errorMessage.value = value;
    }
});

const handleError = () => {
    errorMessage.value = null;
};
</script>

<style scoped>
.danger-border {
    border-color: #fd6d6d;
    color: #d60e0e;
    background-image: url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27%3e%3cpath fill=%27none%27 stroke=%27%23fd6d6d%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%272%27 d=%27M2 5l6 6 6-6%27/%3e%3c/svg%3e");
}

.select_box {
    position: relative;
}

.form-select {
    --bs-form-select-bg-img: none;
    color: #646464;
}

.dark .form-select:focus {
    box-shadow: 0px 0px 0px 2px #655582 !important;
}

.select_box span {
    position: absolute;
    top: 11px;
    right: 15px;
    color: #a9a9a9;
}

.dark .select_box::before {
    color: #b5b9d0;
}

.dark .form-select {
    background-color: #232932 !important;
    border-color: #394251 !important;
    color: #b5b9d0 !important;
}

.dark .form-select:disabled {
    background-color: #2c313b !important;
    border-color: #3f4857 !important;
    color: #8c91a8 !important;
}

.select-box {
    display: none;
    width: calc(100% - var(--bs-gutter-x));
    max-height: 262px;
    border: 1px solid #d7d7d7;
    position: absolute;
    left: 0;
    border-radius: 6px;
    box-shadow: 0px 0px 5px rgb(0 0 0 / 10%);
    margin-top: 6px;
    background: #fff;
    padding: 0px !important;
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 2;
    font-size: 14px !important;
    color: #545454 !important;
    width: 100%;
    font-weight: 500;
}

.dark .select-box {
    background-color: #232932 !important;
    border-color: #394251;
    color: #b5b9d0 !important;
}

.select-box ul {
    padding: 0px;
}

.select-box>li,
.select-box>ul li {
    cursor: pointer;
    padding: 10px 15px;
}

.select-box>li:hover,
.select-box>ul li:hover {
    background: #f8f9fa;
}

.dark .select-box>li:hover,
.dark .select-box>ul li:hover {
    background: #2a323d;
}

.message {
    display: block;
    padding: 10px 15px;
}

.form-select:disabled {
    background-color: #e9ecef;
}

.input_group label {
    margin-bottom: 4px;
    font-weight: 500;
    text-align: left;
    display: block;
}

.form-select {
    box-shadow: none !important;
    padding: 10px 10px;
    font-size: 14px;
    border-color: #d7d7d7;
    font-weight: 500;
    border-radius: 4px;
}

.form-select:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
}

.select_error input {
    border: 1px #df2b2b solid;
    box-shadow: 0px 0px 0px 3px rgb(255 136 136 / 30%) !important;
}

.error_message {
    color: #df2b2b;
    font-weight: 500;
    margin-top: 4px;
    display: block;
}
</style>